<template>
  <main class="p-4">
    <div v-if="loading" id="loading" class="text-center">
      <loading :message="`Loading... ${siteId}`"></loading>
    </div>
    <section v-if="!loading">
      <section v-if="showEnrollment">
        <p class="enrollment-source-text">
          Data represented here is initially sourced from
          <strong>Interactive Voice Response Technology (IRT)</strong>. Once a participant is
          entered in <strong>Electronic Data Capture (EDC)</strong> systems, all enrollment data for
          that participant is sourced from <strong>EDC</strong>.
        </p>
        <b-row id="enrollment-cards">
          <b-col
            v-for="card in enrollmentCards"
            :key="card.title"
            class="mb-2"
            cols="12"
            lg="3"
            md="6"
            sm="6"
            xl="3"
          >
            <enrollment-card
              :color="card.color"
              :icon="card.icon"
              :infoText="card.infoText"
              :number="card.number"
              :source="card.source"
              :title="card.title"
            ></enrollment-card>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="mt-3" cols="12" lg="12" md="12" sm="12" xl="6">
            <comparison-card
              :countryName="countryName"
              :siteId="siteId"
              :studyId="studyId"
              :studyFSFV="studyFSFV"
              :studyActualLSLV="studyActualLSLV"
              :studyPlannedLSLV="studyPlannedLSLV"
              :siteFSFV="siteFSFV"
            ></comparison-card>
          </b-col>
          <b-col class="mt-3" cols="12" lg="12" md="12" sm="12" xl="6">
            <country-statistics
              :countryGoal="countryGoal"
              :siteCountryDetails="siteCountryDetails"
            ></country-statistics>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="mt-3" cols="12" lg="6" md="12" sm="12" xl="6">
            <b-card class="cardStyle total-enrollment-card-height">
              <p class="m-0 card-title">
                <b-icon icon="globe" scale="1.2"></b-icon>&nbsp; Global Study Enrollment
              </p>
              <p class="card-subtitle mb-0 mt-1">
                Hover over a country to see individual country data
              </p>
              <map-chart :countriesData="countriesData" :height="'19rem'"></map-chart>
              <map-cards
                :countries="studyStatistics['countries'].length"
                :globalStudyGoal="globalStudyGoal"
                :randomized="globalRandomized"
                :sites="studyStatistics['sites'].length"
              ></map-cards>
              <div class="countryList" @click="$bvModal.show('showCountryListModal')">
                Show country list
              </div>
            </b-card>
          </b-col>
          <b-col class="mt-3" cols="12" lg="6" md="12" sm="12" xl="6">
            <b-card class="cardStyle total-enrollment-card-height">
              <p class="m-0 pb-4 card-title">
                <b-icon icon="globe" scale="1.2"></b-icon>&nbsp; Study Participants Randomized per
                Country
              </p>
              <div class="echart-wrap" style="height:28rem;">
                <bar-chart
                  :countriesData="countriesData"
                  :height="barChartHeight"
                  :studyCountryGoals="studyCountryGoals"
                ></bar-chart>
              </div>
            </b-card>
          </b-col>
        </b-row>
        <b-row v-show="lastRefresh" class="text-center mt-5">
          <b-col cols="12">
            <div class="lastRefreshText">
              Last data update: <span style="color: blue">{{ lastRefresh }}</span>
            </div>
            <div style="font-size:12px">
              Updates to data contained on this page may be delayed - please contact your Pfizer
              point of contact with questions
            </div>
          </b-col>
        </b-row>
      </section>
      <div v-if="!showEnrollment" id="errors" class="alert alert-info text-center m-4">
        <strong> Enrollment data will be visible once your site is activated.</strong>
      </div>
    </section>
    <b-modal
      id="showCountryListModal"
      body-class="pt-0"
      centered
      hide-footer
      size="lg"
      footerClass="p-2 border-top-0"
      headerClass="p-2 mt-2 border-bottom-0"
      @ok="$bvModal.hide('showCountryListModal')"
    >
      <b-row class="justify-content-center ">
        <b-col cols="10">
          <b-form-input
            id="input-1"
            v-model="filter"
            placeholder="Search by country name"
            type="search"
          ></b-form-input>
        </b-col>
      </b-row>
      <b-row class="justify-content-center mt-3">
        <b-col cols="10" class="">
          <b-table
            id="country-list"
            :sort-by.sync="sortBy"
            :filter="filter"
            :filter-included-fields="filterOn"
            :fields="fields"
            :items="countryList"
            bordered
            small
            class="rounded-lg text-center mt-3"
            empty-text="There are no country list data."
            fixed
            hover
            sticky-header
            responsive="sm"
            show-empty
            striped
            table-variant="light"
          ></b-table>
        </b-col>
      </b-row>
      <template #modal-header="{ ok }">
        <p class="modal-header-text font-weight-bold pb-0 mb-0 pl-1 mx-auto">Country List</p>
        <b-button class="float-right" variant="link-dark" @click="ok()">
          <b-icon icon="x"></b-icon>
        </b-button>
      </template>
    </b-modal>
  </main>
</template>

<script>
  import moment from 'moment';
  import EnrollmentCard from './EnrollmentCard';
  import MapChart from '../charts/MapChart';
  import BarChart from '../charts/BarChart';
  import MapCards from './MapCards';
  import ComparisonCard from './ComparisonCard';
  import CountryStatistics from './CountryStatistics';

  export default {
    name: 'Enrollments',
    components: {
      EnrollmentCard,
      MapChart,
      BarChart,
      MapCards,
      ComparisonCard,
      CountryStatistics,
    },
    props: {
      user: Object,
      studySite: Object,
    },
    data() {
      return {
        filter: '',
        sortBy: 'name',
        sortDesc: true,
        filterOn: ['name'],
        countryList: [],
        error: false,
        loading: false,
        showEnrollment: false,
        siteDetails: {},
        studyStatistics: {},
        siteCountryDetails: {},
        countriesData: {},
        PIName: '',
        countryName: '',
        studyId: '',
        siteId: '',
        barChartHeight: '',
        siteFSFV: 'Not Available',
        studyFSFV: 'Not Available',
        studyActualLSLV: '',
        studyPlannedLSLV: '',
        lastRefresh: '',
        enrollmentCards: [],
        countryGoal: '-',
        studyCountryGoals: [],
        globalStudyGoal: 0,
        fields: [
          { key: 'name', label: 'Country', sortable: true },
          { key: 'screened', label: 'Screened', sortable: true },
          { key: 'screenFailed', label: 'ScreenFailed', sortable: true },
          { key: 'randomized', label: 'Randomized', sortable: true },
          { key: 'completed', label: 'Completed', sortable: true },
        ],
      };
    },
    metaInfo() {
      let metaTitle = this.$appName + this.studyId + '-' + this.siteId + ' Enrollment';
      return {
        title: metaTitle,
      };
    },
    computed: {
      globalRandomized() {
        return this.studyStatistics['total']['enrollmentData'] !== null
          ? this.studyStatistics['total']['enrollmentData']['randomized']
          : '-';
      },
    },
    watch: {
      $route() {
        this.setRouteParams();
        this.initialize();
      },
    },
    created() {
      this.loading = true;
      this.showEnrollment = false;
      this.setRouteParams();
      this.initialize();
    },
    methods: {
      setRouteParams() {
        this.studyId = this.$route.params['studyId'];
        this.siteId = this.$route.params['siteId'];
      },
      initialize() {
        this.loading = true;
        this.showEnrollment = false;
        this.siteCountryDetails = {};
        this.countryGoal = '-';
        let endpoints = [
          `/api/get-site-milestones?study=${this.studyId}&site=${this.siteId}`,
          `/api/get-study-statistics/${this.studyId}`,
          `/api/get-study-country-goals/${this.studyId}`,
          `/api/get-study-goal/${this.studyId}`,
          `/api/cordis/milestones/${this.studyId}`,
        ];
        Promise.allSettled(endpoints.map(endpoint => axios.get(endpoint)))
          .then(
            axios.spread((...allData) => {
              let [
                siteMilestones,
                statistics,
                studyCountryGoals,
                studyGoal,
                studyMilestones,
              ] = allData;
              if (studyCountryGoals !== undefined && studyCountryGoals.status === 'fulfilled') {
                this.studyCountryGoals = studyCountryGoals.value['data']['items'];
              }
              if (siteMilestones !== undefined && siteMilestones.status === 'fulfilled') {
                this.setSiteFSFV(siteMilestones);
              } else {
                this.siteFSFV = 'Not Available';
              }
              if (statistics.status === 'fulfilled' && statistics.value.status !== 204) {
                this.setupDetails(statistics.value.data['data']);
              } else {
                this.loading = false;
              }
              if (studyGoal.status === 'fulfilled' && studyGoal.value.status !== 204) {
                this.globalStudyGoal = studyGoal.value['data']['data']['goal'];
              }
              if (studyMilestones.status === 'fulfilled' && studyMilestones.value.status !== 204) {
                this.studyFSFV = moment(
                  studyMilestones.value['data']['studyMilestones']['actualFSFV'],
                ).format('D MMM YYYY');
                this.studyActualLSLV = moment(
                  studyMilestones.value['data']['studyMilestones']['actualLSLV'],
                ).format('D MMM YYYY');
                this.studyPlannedLSLV = moment(
                  studyMilestones.value['data']['studyMilestones']['plannedLSLV'],
                ).format('D MMM YYYY');
              }
            }),
          )
          .catch(error => {
            console.error(error);
            this.error = true;
            this.loading = false;
          });
      },
      setSiteFSFV(siteMilestones) {
        let findFSFV = siteMilestones.value['data']['items'].find(
          item =>
            item['milestoneName'] === 'Site FSFV Date' || item['milestoneName'] === 'Site FSFV',
        );
        if (findFSFV && findFSFV['actualDate']) {
          this.siteFSFV = moment(findFSFV['actualDate']).format('D MMM YYYY');
        } else {
          this.siteFSFV = 'Not Available';
        }
      },
      async setupDetails(data) {
        this.studyStatistics = data;
        this.siteDetails = data['sites'].find(
          element => element['siteInfo']['siteNumber'] === this.$route.params['siteId'],
        );
        if (this.siteDetails !== undefined) {
          await this.setupSiteDetails(data);
        }
        this.countriesData = this.studyStatistics['countries'];
        this.countriesData.forEach(el => {
          this.countryList.push({
            name: el['country'],
            completed: el['enrollmentData']['completed'],
            randomized: el['enrollmentData']['randomized'],
            screenFailed: el['enrollmentData']['screenFailed'],
            screened: el['enrollmentData']['screened'],
          });
        });
        let barChartItemHeight = this.studyCountryGoals.length > 0 ? 5 : 2.5;
        this.barChartHeight =
          Object.keys(this.countriesData).length * barChartItemHeight + 2 + 'rem';
        this.loading = false;
      },
      async setupSiteDetails(data) {
        this.showEnrollment = true;
        this.siteCountryDetails = data['countries'].find(
          element => element['country'] === this.siteDetails['siteInfo']['countryName'],
        );
        this.lastRefresh =
          this.siteDetails['enrollmentData']['lastRefresh'] !== null
            ? moment(this.siteDetails['enrollmentData']['lastRefresh'])
            : 'Unavailable';
        this.PIName = `(${this.siteDetails['siteInfo']['investigatorName']})`;
        this.setupEnrollmentCards(this.siteDetails['enrollmentData']);
        this.countryName = this.siteCountryDetails.country;
        if (this.countryName !== undefined) {
          await this.getStudyCountryGoals();
        }
      },

      async getStudyCountryGoals() {
        try {
          let response = await axios.get(
            `/api/get-study-country-goals/${this.studyId}/${this.countryName}`,
          );
          if (response.status !== 204) {
            this.countryGoal = response['data']['data']['goal'];
          }
        } catch (error) {
          console.error(error);
        }
      },
      setupEnrollmentCards(enrollmentData) {
        let screenFailRate = '';
        if (enrollmentData['screened'] !== 0) {
          screenFailRate =
            ((enrollmentData['screenFailed'] / enrollmentData['screened']) * 100).toFixed(1) + '%';
        }
        this.enrollmentCards = [
          {
            color: 'primaryLight',
            icon: 'person-plus',
            infoText: 'FSFV',
            number: enrollmentData['screened'],
            source: this.siteFSFV,
            title: 'Screened',
          },
          {
            color: 'danger',
            icon: 'person-x',
            infoText: 'Screen Fail Rate',
            number: enrollmentData['screenFailed'],
            source: screenFailRate,
            title: 'Screen Failed',
          },
          {
            color: 'warning',
            icon: 'person',
            infoText: 'Planned',
            number: enrollmentData['randomized'],
            source: this.siteDetails['siteInfo']['plannedSubject'],
            title: 'Randomized',
          },
          {
            color: 'success',
            icon: 'person-check',
            infoText: 'Ongoing',
            number: enrollmentData['completed'],
            source:
              enrollmentData['randomized'] -
              enrollmentData['discontinued'] -
              enrollmentData['completed'],
            title: 'Completed',
          },
        ];
      },
    },
  };
</script>

<style>
  .card-subtitle {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    font-feature-settings: 'case on';
    color: #747778;
  }

  .echart-wrap {
    overflow-y: auto;
  }

  .map-chart-inside-card {
    height: 3rem;
    border-radius: 12px;
    background: rgba(191, 191, 241, 0.12);
    border: 0;
    padding: 0px;
  }

  .comparison-inside-card {
    height: 3rem;
    border-radius: 12px;
    background: #fffbfe;
    border: 0;
    padding: 0px;
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0.25px;
    color: #6f6f6f;
  }

  .map-card-text {
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    color: #6f6f6f;
    padding: 0.8rem;
  }

  .map-card-number {
    font-weight: 500;
    font-size: 24px;
    color: #0095ff;
  }

  .comparison-card-number {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
  }

  .card-title {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: #0060aa;
  }

  @media (min-width: 992px) {
    .total-enrollment-card-height {
      height: 34rem;
    }
  }

  .lastRefreshText {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.5px;
    color: #006a68;
  }

  .enrollment-source-text {
    font-weight: 400;
    font-size: 13px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.5px;
    color: #383838;
    text-align: center;
  }
  .countryList {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    letter-spacing: 0.25px;
    text-decoration-line: underline;
    color: #3c45ea;
    width: 100%;
    cursor: pointer;
    margin-top: 0.3rem;
  }
</style>
